/**
 * AdminPaymentsScreen — registro manual de pagos.
 *
 * - Top toolbar: search por email/id, filter por mes (últimos 12), botón "+ Registrar pago".
 * - Tabla con fecha, user, tier, monto, método, nota, eliminar.
 * - Card "Total mes" con sum del mes filtrado (o total visible si filter "todos").
 * - Modal "Registrar pago": user-select por listUsers, tier, monto, método, fecha, nota.
 *
 * El backend NO acepta filter por mes (solo user_id/limit/offset). Hacemos el
 * filtrado de mes client-side sobre la primera página (limit grande).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "../../lib/toast";
import { Icon } from "../../lib/Icon";
import { Avatar, EmptyState } from "../../lib/components";
import {
  createPayment,
  deletePayment,
  downloadBlob,
  exportCsv,
  listPayments,
  listUsers,
  type AdminPayment,
  type AdminUserSummary,
  type CreatePaymentPayload,
} from "../../api/admin";
import {
  AdminBadge,
  AdminGate,
  Skeleton,
  colorFor,
  formatCOP,
  formatDate,
  initialsOf,
  tierLabel,
} from "./_shared";

const PAGE_SIZE = 200;

export function AdminPaymentsScreen() {
  return (
    <AdminGate>
      <Inner />
    </AdminGate>
  );
}

function Inner() {
  const toast = useToast();
  const [payments, setPayments] = useState<AdminPayment[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [monthFilter, setMonthFilter] = useState<string>(() => currentYearMonth());
  const [exporting, setExporting] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const list = await listPayments({ limit: PAGE_SIZE, offset: 0 });
      setPayments(list);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const monthOptions = useMemo(() => buildMonthOptions(12), []);

  const filtered = useMemo(() => {
    if (!payments) return [];
    const q = search.trim().toLowerCase();
    return payments.filter((p) => {
      // Filtro mes: matchea por created_at "YYYY-MM"
      if (monthFilter !== "all") {
        const ym = (p.created_at || "").slice(0, 7);
        if (ym !== monthFilter) return false;
      }
      // Filtro search: email o user_id
      if (q) {
        if (String(p.user_id) === q) return true;
        if ((p.user_email || "").toLowerCase().includes(q)) return true;
        return false;
      }
      return true;
    });
  }, [payments, search, monthFilter]);

  const totalMonth = useMemo(() => filtered.reduce((sum, p) => sum + (p.amount_cop || 0), 0), [filtered]);

  const onDelete = useCallback(
    async (id: number) => {
      if (!window.confirm("¿Eliminar este pago? Esta acción no se puede deshacer.")) return;
      try {
        await deletePayment(id);
        setPayments((prev) => (prev ? prev.filter((p) => p.id !== id) : prev));
        toast("Pago eliminado");
      } catch (e) {
        toast("No pude eliminar: " + (e instanceof Error ? e.message : ""));
      }
    },
    [toast],
  );

  const onCreated = useCallback(
    (p: AdminPayment) => {
      setPayments((prev) => (prev ? [p, ...prev] : [p]));
      setShowCreate(false);
      toast(`Pago de $${p.amount_cop.toLocaleString("es-CO")} registrado`);
    },
    [toast],
  );

  const onExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const blob = await exportCsv("payments");
      const stamp = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `aetherlite-payments-${stamp}.csv`);
      toast("CSV de pagos descargado");
    } catch (e) {
      toast("No pude exportar: " + (e instanceof Error ? e.message : ""));
    } finally {
      setExporting(false);
    }
  }, [exporting, toast]);

  const monthLabel = monthFilter === "all"
    ? "todos los meses"
    : monthOptions.find((m) => m.value === monthFilter)?.label || monthFilter;

  return (
    <div className="page al-admin-shell">
      <div className="al-admin-head">
        <div>
          <h1 className="al-page-title">
            Pagos <AdminBadge />
          </h1>
          <p className="muted">Registro manual de pagos de coaches.</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn btn-secondary" onClick={onExport} disabled={exporting}>
            <Icon name="upload" size={14} /> {exporting ? "Exportando…" : "Exportar (CSV)"}
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <Icon name="plus" size={14} /> Registrar pago
          </button>
        </div>
      </div>

      {/* Total mes */}
      <div className="card al-admin-total">
        <div>
          <div className="stat-label">Total {monthLabel}</div>
          <div className="stat-value">{formatCOP(totalMonth)}</div>
          <div className="tiny muted">
            {filtered.length} pago{filtered.length === 1 ? "" : "s"}{search ? ` para “${search}”` : ""}
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="card al-admin-toolbar">
        <div className="search-box" style={{ flex: 1, minWidth: 200 }}>
          <Icon name="search" size={16} />
          <input
            placeholder="Buscar por email o user_id…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="field" style={{ minWidth: 180 }}>
          <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
            <option value="all">Todos los meses</option>
            {monthOptions.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabla */}
      <div className="card al-admin-card-flush">
        {loading && !payments ? (
          <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} height={48} />)}
          </div>
        ) : err && !payments ? (
          <EmptyState
            icon="money"
            title="No pude cargar pagos"
            body={err}
            action={
              <button className="btn btn-primary" onClick={load}>
                Reintentar
              </button>
            }
          />
        ) : filtered.length === 0 ? (
          <div style={{ padding: 32 }}>
            <EmptyState
              icon="money"
              title="Sin pagos en este filtro"
              body="Registra el primer pago manual con el botón “+ Registrar pago”."
            />
          </div>
        ) : (
          <table className="al-admin-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Coach</th>
                <th className="hide-mobile">Tier</th>
                <th>Monto</th>
                <th className="hide-mobile">Método</th>
                <th className="hide-mobile">Nota</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id}>
                  <td>{formatDate(p.created_at)}</td>
                  <td>
                    <div className="al-admin-user-cell">
                      <Avatar
                        user={{
                          initials: initialsOf(null, p.user_email),
                          avatar_color: colorFor(p.user_email || String(p.user_id)),
                        }}
                      />
                      <div style={{ minWidth: 0 }}>
                        <div className="tiny" style={{ fontWeight: 600 }}>
                          {p.user_email || `user #${p.user_id}`}
                        </div>
                        <div className="tiny muted">#{p.user_id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="hide-mobile">
                    <span className="chip">{tierLabel(p.plan_tier)}</span>
                  </td>
                  <td>
                    <b>${p.amount_cop.toLocaleString("es-CO")}</b>
                    <div className="tiny muted">{p.currency || "COP"}</div>
                  </td>
                  <td className="hide-mobile">{p.method || "—"}</td>
                  <td className="hide-mobile" style={{ maxWidth: 200 }}>
                    <span className="al-admin-note" title={p.notes || ""}>
                      {p.notes || "—"}
                    </span>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      className="btn btn-ghost btn-sm"
                      title="Eliminar pago"
                      onClick={() => onDelete(p.id)}
                    >
                      <Icon name="x" size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CreatePaymentModal
          onClose={() => setShowCreate(false)}
          onCreated={onCreated}
        />
      )}
    </div>
  );
}

// ============= helpers =============

function currentYearMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function buildMonthOptions(count: number): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("es-CO", { year: "numeric", month: "long" });
    out.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return out;
}

// ============= Modal crear pago =============

interface CreatePaymentModalProps {
  onClose: () => void;
  onCreated: (p: AdminPayment) => void;
}

const TIER_PRICES: Record<string, number> = {
  solo: 39000,
  pro: 39000,
  studio: 79000,
};

function CreatePaymentModal({ onClose, onCreated }: CreatePaymentModalProps) {
  const toast = useToast();
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [userQuery, setUserQuery] = useState("");
  const [userId, setUserId] = useState<number | null>(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<number | null>(null);

  const [tier, setTier] = useState<string>("solo");
  const [amount, setAmount] = useState<number>(TIER_PRICES.solo);
  const [method, setMethod] = useState<string>("transferencia");
  const [paidAt, setPaidAt] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  // Buscar users con debounce 300ms
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (!userQuery.trim()) {
      setUsers([]);
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      setSearching(true);
      listUsers({ q: userQuery.trim(), limit: 10 })
        .then((list) => setUsers(list))
        .catch(() => setUsers([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [userQuery]);

  const selectedUser = useMemo(
    () => users.find((u) => u.id === userId) || null,
    [users, userId],
  );

  const onChangeTier = (newTier: string) => {
    setTier(newTier);
    if (TIER_PRICES[newTier]) setAmount(TIER_PRICES[newTier]);
  };

  const onSubmit = async () => {
    if (!userId) {
      toast("Seleccioná un coach primero");
      return;
    }
    if (!amount || amount <= 0) {
      toast("Monto inválido");
      return;
    }
    setSubmitting(true);
    try {
      const payload: CreatePaymentPayload = {
        user_id: userId,
        amount_cop: Math.round(amount),
        plan_tier: tier,
        period_start: paidAt,
        method,
        notes: notes.trim() || null,
      };
      const res = await createPayment(payload);
      onCreated(res);
    } catch (e) {
      toast("No pude registrar el pago: " + (e instanceof Error ? e.message : ""));
      setSubmitting(false);
    }
  };

  return (
    <div className="al-modal-backdrop" onClick={() => !submitting && onClose()}>
      <div className="al-modal al-admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="card-header">
          <div className="card-title">Registrar pago</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={submitting}>
            <Icon name="x" size={14} />
          </button>
        </div>

        <div className="field" style={{ position: "relative" }}>
          <label>Coach</label>
          {selectedUser ? (
            <div className="al-admin-userselect-pill">
              <Avatar
                user={{
                  initials: initialsOf(selectedUser.name, selectedUser.email),
                  avatar_color: colorFor(selectedUser.email),
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="tiny" style={{ fontWeight: 600 }}>
                  {selectedUser.name || selectedUser.email}
                </div>
                <div className="tiny muted">{selectedUser.email}</div>
              </div>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setUserId(null);
                  setUserQuery("");
                  setShowUserDropdown(true);
                }}
              >
                Cambiar
              </button>
            </div>
          ) : (
            <>
              <input
                placeholder="Buscar por email o nombre…"
                value={userQuery}
                onChange={(e) => {
                  setUserQuery(e.target.value);
                  setShowUserDropdown(true);
                }}
                onFocus={() => setShowUserDropdown(true)}
              />
              {showUserDropdown && (userQuery.trim() || searching) && (
                <div className="al-admin-userselect-list">
                  {searching ? (
                    <div className="muted tiny" style={{ padding: 8 }}>Buscando…</div>
                  ) : users.length === 0 ? (
                    <div className="muted tiny" style={{ padding: 8 }}>Sin resultados</div>
                  ) : (
                    users.map((u) => (
                      <button
                        type="button"
                        key={u.id}
                        className="al-admin-userselect-item"
                        onClick={() => {
                          setUserId(u.id);
                          setUserQuery("");
                          setShowUserDropdown(false);
                        }}
                      >
                        <Avatar
                          user={{
                            initials: initialsOf(u.name, u.email),
                            avatar_color: colorFor(u.email),
                          }}
                        />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div className="tiny" style={{ fontWeight: 600 }}>{u.name || u.email}</div>
                          <div className="tiny muted">{u.email} · {tierLabel(u.plan_tier)}</div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="al-admin-form-grid">
          <div className="field">
            <label>Tier</label>
            <select value={tier} onChange={(e) => onChangeTier(e.target.value)}>
              <option value="solo">Solo</option>
              <option value="pro">Pro</option>
              <option value="studio">Studio</option>
            </select>
          </div>
          <div className="field">
            <label>Monto (COP)</label>
            <input
              type="number"
              min={0}
              step={1000}
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
            />
          </div>
          <div className="field">
            <label>Método</label>
            <select value={method} onChange={(e) => setMethod(e.target.value)}>
              <option value="transferencia">Transferencia</option>
              <option value="pse">PSE</option>
              <option value="nequi">Nequi</option>
              <option value="efectivo">Efectivo</option>
              <option value="card">Tarjeta</option>
              <option value="otro">Otro</option>
            </select>
          </div>
          <div className="field">
            <label>Fecha</label>
            <input
              type="date"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <label>Nota (opcional)</label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ej: pago anticipado por 3 meses"
          />
        </div>

        <div className="al-modal-actions">
          <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={submitting || !userId}
          >
            {submitting ? "Registrando…" : "Registrar pago"}
          </button>
        </div>
      </div>
    </div>
  );
}
